import React, { useState } from "react";
import {
  Modal,
  Box,
  Typography,
  IconButton,
  FormControlLabel,
  LinearProgress,
} from "@mui/material";
import { hasRole } from "../../Helpers/authHelpers";
import { UserRole } from "../../Enums/companyStatus";
import { Pipeline } from "../../Enums/pipeline";
import { useParams } from "react-router-dom";
import { Document } from "../../Models/company";
import CloseIcon from "@mui/icons-material/Close";
import FileUploader from "./FileUploader";
import { useTranslation } from "react-i18next";
import {
  allowedExtensions,
  MAX_FILE_SIZE,
} from "../../Constants/fileExtensions";
import { enqueueSnackbar } from "notistack";
import {
  downloadChunks,
  downloadDocumentBlob,
  isPreviewableFile,
} from "../../Helpers/documentHelper";
import { useTerminology } from "../../Core/useTerminology";
import LogViewerModal from "./LogViewer";
import PdfPreviewModal from "../Common/PdfPreviewModal";
import ImagePreviewModal from "../Common/ImagePreviewModal";
import NewFilesList from "./NewFileList";
import {
  useDocuments,
  useUploadDocument,
  useDeleteDocument,
  useDownloadDocument,
  useStopDocument,
  useStorageQuota,
  useDetachDocumentFromQuestion,
} from "../../Query/useCompanies";
import color from "../../Constants/colors";
import { SortControls } from "../Common/SortControls";
import { ComoSelectOption } from "../Common/ComoSelect";
import UploadedDocumentsList from "./UploadedDocumentsList";
import { SortBy, SortOrder } from "../../Enums/document";
import { ComoSwitch } from "../Common/ComoSwitch";
import { fetchDocumentChunks } from "../../Api/documents";

interface FileUploadModalProps {
  open: boolean;
  onClose: () => void;
  documents: Document[];
}

const FileUploadModal: React.FC<FileUploadModalProps> = ({ open, onClose }) => {
  const { t } = useTranslation("documentUpload");
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [uploadedFilesPage, setUploadedFilesPage] = useState(0);
  const [uploadedFilesRowsPerPage] = useState(10);
  const { companyId } = useParams<{ companyId: string }>();
  const [sortBy, setSortBy] = useState<SortBy>(SortBy.Name);
  const [sortOrder, setSortOrder] = useState<SortOrder>(SortOrder.Asc);
  const terminology = useTerminology();
  const [logModalOpen, setLogModalOpen] = useState(false);
  const [selectedDocumentId, setSelectedDocumentId] = useState<
    number | undefined
  >(undefined);
  const [pdfPreviewOpen, setPdfPreviewOpen] = useState(false);
  const [imagePreviewOpen, setImagePreviewOpen] = useState(false);
  const [previewDocumentId, setPreviewDocumentId] = useState<number | null>(
    null
  );
  const [previewDocumentName, setPreviewDocumentName] = useState<string>("");
  const { data: storageQuota, refetch: refetchQuota } =
    useStorageQuota(companyId);

  const isAdmin = hasRole(UserRole.ADMIN);
  const [pipeline, setPipeline] = useState<Pipeline>(Pipeline.LEGACY);

  const { data: documentsData } = useDocuments(
    companyId!,
    uploadedFilesPage + 1,
    uploadedFilesRowsPerPage,
    sortBy.toString(),
    sortOrder.toString(),
    false
  );

  const uploadDocumentMutation = useUploadDocument(companyId!);
  const deleteDocumentMutation = useDeleteDocument(companyId!);
  const downloadDocumentMutation = useDownloadDocument();
  const stopDocumentMutation = useStopDocument(companyId!);
  const detachDocumentMutation = useDetachDocumentFromQuestion(companyId!);

  const handleSubmit = () => {
    const oversizedFile = uploadedFiles.find(
      (file) => file.size > MAX_FILE_SIZE
    );

    if (oversizedFile) {
      const sizeInMegaBytes = MAX_FILE_SIZE / (1024 * 1024);

      enqueueSnackbar(
        `File "${oversizedFile.name}" exceeds the ${sizeInMegaBytes} MB limit`,
        { variant: "error" }
      );
      return;
    }

    const uploadPromises = uploadedFiles.map(async (file) => {
      const formData = new FormData();
      formData.append("company_id", companyId!);
      formData.append("document_name", file.name);
      formData.append("file", file);
      formData.append("pipeline", pipeline);

      return uploadDocumentMutation.mutateAsync(formData);
    });

    Promise.allSettled(uploadPromises).then((results) => {
      let successCount = 0;
      let failedCount = 0;
      let alreadyUploadedCount = 0;
      let rejectedCount = 0;

      results.forEach((result, index) => {
        if (result.status === "fulfilled") {
          successCount++;
          handleRemoveFile(uploadedFiles[index].name);
        } else {
          const error = result.reason;

          if (error?.response?.status === 409) {
            alreadyUploadedCount++;
          } else if (error?.response?.status === 413) {
            rejectedCount++;
          } else {
            failedCount++;
          }
        }
      });

      if (successCount > 0) {
        enqueueSnackbar(
          t("documentUpload.filesUploadedSuccess", { count: successCount }),
          { variant: "success" }
        );
      }

      if (alreadyUploadedCount > 0) {
        enqueueSnackbar(
          t("documentUpload.filesAlreadyUploaded", {
            count: alreadyUploadedCount,
          }),
          { variant: "warning" }
        );
      }

      if (rejectedCount > 0) {
        enqueueSnackbar(
          t(
            `documentUpload.filesRejectedTooLarge_${rejectedCount === 1 ? "one" : "other"}`,
            {
              count: rejectedCount,
            }
          ),
          { variant: "error" }
        );
      }

      if (failedCount > 0) {
        enqueueSnackbar(
          t("documentUpload.filesUploadFailed", { count: failedCount }),
          { variant: "error" }
        );
      }
      refetchQuota();
    });
  };

  const handleRemoveFile = (fileName: string) => {
    setUploadedFiles((prevFiles) =>
      prevFiles.filter((file) => file.name !== fileName)
    );
  };

  const handleDeleteDocument = (documentId: number) => {
    deleteDocumentMutation.mutate(documentId, {
      onSuccess: () => {
        if (documentsData) {
          const remainingDocuments = documentsData.documents.length - 1;
          const newPage =
            uploadedFilesPage > 0 &&
            remainingDocuments % uploadedFilesRowsPerPage === 0
              ? uploadedFilesPage - 1
              : uploadedFilesPage;

          setUploadedFilesPage(newPage);
        }
      },
    });
  };

  const handleStopDocument = (website_id: number) => {
    stopDocumentMutation.mutate(website_id);
  };

  const handleDownloadDocument = (documentId: number, fileName: string) => {
    downloadDocumentMutation.mutate(
      { documentId },
      {
        onSuccess: (response) => {
          downloadDocumentBlob(response, fileName);
        },
      }
    );
  };

  const handleDetachDocument = (documentId: number) => {
    detachDocumentMutation.mutate(documentId);
  };

  const handleDownloadChunks = async (documentId: number) => {
    const chunks = await fetchDocumentChunks(documentId);

    downloadChunks(chunks, documentId);
  };

  const handleViewLogs = (documentId: number) => {
    setSelectedDocumentId(documentId);
    setLogModalOpen(true);
  };

  const handlePreviewDocument = (documentId: number, fileName: string) => {
    if (!isPreviewableFile(fileName)) return;

    setPreviewDocumentId(documentId);
    setPreviewDocumentName(fileName);

    if (fileName.toLowerCase().endsWith(".pdf")) {
      setPdfPreviewOpen(true);
    } else {
      setImagePreviewOpen(true);
    }
  };

  const sortByOptions: ComoSelectOption[] = [
    { id: SortBy.Name, label: t("documentUpload.name") },
    { id: SortBy.Date, label: t("documentUpload.date") },
  ];

  const sortOrderOptions: ComoSelectOption[] = [
    { id: SortOrder.Asc, label: t("documentUpload.ascending") },
    { id: SortOrder.Desc, label: t("documentUpload.descending") },
  ];

  return (
    <Modal
      open={open}
      onClose={onClose}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        padding: "20px",
      }}
    >
      <Box
        sx={{
          backgroundColor: "white",
          borderRadius: "8px",
          border: `1px solid ${color.gray200}`,
          width: "100%",
          maxWidth: "1200px",
          maxHeight: "90vh",
          display: "flex",
          flexDirection: "column",
          boxShadow:
            "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "16px 24px",
            borderBottom: `1px solid ${color.gray200}`,
          }}
        >
          <Typography
            variant="h6"
            sx={{
              margin: 0,
              fontSize: "1.25rem",
              fontWeight: 600,
              color: color.primary_dark,
            }}
          >
            {t("documentUpload.fileManagement")}
          </Typography>
          <IconButton
            onClick={onClose}
            sx={{
              border: "none",
              background: "none",
              cursor: "pointer",
              padding: "8px",
              borderRadius: "4px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              "&:hover": {
                backgroundColor: color.gray100,
              },
            }}
          >
            <CloseIcon sx={{ color: color.gray500 }} />
          </IconButton>
        </Box>

        <Box
          sx={{
            display: "flex",
            flex: 1,
            overflow: "hidden",
            minHeight: "500px",
          }}
        >
          <Box
            sx={{
              flex: "0 0 45%",
              padding: "24px",
              borderRight: `1px solid ${color.gray200}`,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            <Typography
              variant="subtitle1"
              sx={{
                margin: "0 0 16px 0",
                fontSize: "1rem",
                fontWeight: 600,
                color: color.gray900,
              }}
            >
              {t("documentUpload.uploadNewDocuments")}
            </Typography>

            <Box
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
              }}
            >
              <FileUploader
                uploadedFiles={uploadedFiles}
                setUploadedFiles={setUploadedFiles}
                allowedExtensions={allowedExtensions}
                disabled={uploadDocumentMutation.isPending}
                showUploadImage={uploadedFiles.length === 0}
                text={true}
              />
              <Typography
                variant="body2"
                sx={{
                  mt: 2,
                  textAlign: "center",
                  color: color.primary_dark,
                  fontStyle: "italic",
                }}
              >
                {t("documentUpload.sizeLimitInfo")}
              </Typography>
              {storageQuota && (
                <Box sx={{ mt: 2 }}>
                  <Typography
                    variant="body2"
                    sx={{ mb: 0.5, color: color.gray700 }}
                  >
                    {t("documentUpload.storageLimit")} {storageQuota.consumed}{" "}
                    MB /{" "}
                    {storageQuota.limit === -1
                      ? "∞"
                      : `${storageQuota.limit} MB`}
                  </Typography>

                  {storageQuota.limit !== -1 && (
                    <LinearProgress
                      variant="determinate"
                      value={(storageQuota.consumed / storageQuota.limit) * 100}
                      sx={{
                        height: 6,
                        borderRadius: 3,
                        backgroundColor: color.gray200,
                        "& .MuiLinearProgress-bar": {
                          backgroundColor: color.accent,
                          borderRadius: 3,
                        },
                      }}
                    />
                  )}
                </Box>
              )}

              {isAdmin && (
                <FormControlLabel
                  control={
                    <ComoSwitch
                      checked={pipeline === Pipeline.MISTRAL}
                      onChange={(e) =>
                        setPipeline(
                          e.target.checked ? Pipeline.MISTRAL : Pipeline.LEGACY
                        )
                      }
                    />
                  }
                  label={t("documentUpload.useMistralPipeline")}
                  sx={{
                    ml: 0.5,
                    mt: 1,
                    mb: 1,
                    "& .MuiFormControlLabel-label": {
                      fontSize: "0.8rem",
                      color: color.gray500,
                    },
                  }}
                />
              )}
              <NewFilesList
                files={uploadedFiles}
                onRemoveFile={handleRemoveFile}
                onSubmit={handleSubmit}
                isUploading={uploadDocumentMutation.isPending}
              />
            </Box>
          </Box>

          <Box
            sx={{
              flex: "0 0 55%",
              padding: "24px",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            <Typography
              variant="subtitle1"
              sx={{
                margin: "0 0 16px 0",
                fontSize: "1rem",
                fontWeight: 600,
                color: color.gray900,
              }}
            >
              {t("documentUpload.entityDocuments", {
                entity: terminology.singular,
              })}
            </Typography>

            <SortControls
              sortBy={sortBy}
              sortOrder={sortOrder}
              sortByOptions={sortByOptions}
              sortOrderOptions={sortOrderOptions}
              onSortByChange={(value) => setSortBy(value as SortBy)}
              onSortOrderChange={(value) => setSortOrder(value as SortOrder)}
            />

            <UploadedDocumentsList
              documents={documentsData?.documents || []}
              total={documentsData?.total || 0}
              page={uploadedFilesPage}
              rowsPerPage={uploadedFilesRowsPerPage}
              onPageChange={setUploadedFilesPage}
              onStopDocument={handleStopDocument}
              onViewLogs={handleViewLogs}
              onDownloadDocument={handleDownloadDocument}
              onDownloadChunks={handleDownloadChunks}
              onDeleteDocument={handleDeleteDocument}
              onPreviewDocument={handlePreviewDocument}
              onDetachDocument={handleDetachDocument}
              isDownloading={downloadDocumentMutation.isPending}
            />
          </Box>
        </Box>

        <LogViewerModal
          open={logModalOpen}
          onClose={() => setLogModalOpen(false)}
          documentId={selectedDocumentId}
        />
        <PdfPreviewModal
          open={pdfPreviewOpen}
          onClose={() => setPdfPreviewOpen(false)}
          documentId={previewDocumentId}
          documentName={previewDocumentName}
        />
        <ImagePreviewModal
          open={imagePreviewOpen}
          onClose={() => setImagePreviewOpen(false)}
          documentId={previewDocumentId}
          documentName={previewDocumentName}
        />
      </Box>
    </Modal>
  );
};

export default FileUploadModal;
